/**
 * Company Notes Tab
 *
 * Manages notes for a company with add and delete functionality.
 */

import { format, parseISO } from 'date-fns';
import { Plus, StickyNote, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCompanyNotes, useCreateNote, useDeleteNote } from '../hooks';

interface Props {
  companyId: string;
}

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

export default function CompanyNotesTab({ companyId }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');

  const { data: notes, isLoading } = useCompanyNotes(companyId);
  const createNote = useCreateNote();
  const deleteNote = useDeleteNote();

  const handleOpenCreate = () => {
    setNoteContent('');
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setNoteContent('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteContent.trim()) return;

    try {
      await createNote.mutateAsync({
        companyId,
        data: { content: noteContent.trim() },
      });
      handleClose();
    } catch (err) {
      console.error('Failed to create note:', err);
    }
  };

  const handleDelete = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteNote.mutateAsync({ companyId, noteId });
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500">Loading notes...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Notes</h3>
        <Button onClick={handleOpenCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Note
        </Button>
      </div>

      {!notes?.length ? (
        <Card>
          <CardContent className="py-8 text-center">
            <StickyNote className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No notes yet</p>
            <Button variant="link" onClick={handleOpenCreate} className="mt-2">
              Add the first note
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <Card key={note.id}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="whitespace-pre-wrap text-sm">{note.content}</div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      <User className="h-3 w-3" />
                      <span>{note.author?.name || 'Unknown'}</span>
                      <span>-</span>
                      <span>{formatDate(note.created_at)}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(note.id)}
                    className="text-red-600 hover:text-red-700 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Note Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>Add a note to this company</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="content">Note</Label>
              <Textarea
                id="content"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Enter your note..."
                rows={4}
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={createNote.isPending || !noteContent.trim()}>
                Add Note
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
